import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, TrendingUp, AlertTriangle, ArrowRight, Trophy } from "lucide-react";
import { API } from "@/App";

const VERDICT_STYLE = {
  "Strong Draw": { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", icon: TrendingUp },
  "Likely Draw": { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", icon: TrendingUp },
  "Likely Home": { bg: "bg-blue-100",    text: "text-blue-700",    border: "border-blue-200",    icon: TrendingUp },
  "Likely Away": { bg: "bg-indigo-100",  text: "text-indigo-700",  border: "border-indigo-200",  icon: TrendingUp },
  "Risky/Avoid": { bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-300",   icon: AlertTriangle },
};

const AiInsightsSection = () => {
  const [insights, setInsights] = useState([]);
  const [weekNumber, setWeekNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/ai-insights?limit=10`)
      .then(r => { setInsights(r.data?.insights || []); setWeekNumber(r.data?.week_number); setLoading(false); })
      .catch(() => { setInsights([]); setLoading(false); });
  }, []);

  if (loading) return null;
  if (!insights.length) return null;

  return (
    <section className="py-8 px-4" data-testid="ai-insights-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <Badge className="bg-violet-600 text-white mb-2 inline-flex items-center gap-1.5" data-testid="ai-insights-badge">
            <Brain className="h-3 w-3" /> AI ANALYZER {weekNumber ? ` · WEEK ${weekNumber}` : ""}
          </Badge>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Football AI Top Picks{weekNumber ? ` — Week ${weekNumber}` : ""}</h2>
          <p className="text-sm text-gray-500 mt-1">Auto-analyzed using past statistics, head-to-head, and probability scoring.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {insights.map((p, i) => {
            const style = VERDICT_STYLE[p.verdict] || VERDICT_STYLE["Likely Draw"];
            const Icon = style.icon;
            return (
              <Card key={i} className={`border ${style.border} ${style.bg}/40`} data-testid={`ai-insight-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                    <Badge className={`${style.bg} ${style.text} text-[10px] font-bold border ${style.border}`}>
                      <Icon className="h-3 w-3 mr-1 inline" /> {p.verdict}
                    </Badge>
                    {p.coupon_number && <Badge className="bg-gray-900 text-white text-[10px]">Coupon #{p.coupon_number}</Badge>}
                  </div>
                  <p className="font-bold text-gray-900 text-sm leading-tight">{p.home_team} vs {p.away_team}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{p.league}</p>
                  {p.reasoning && (
                    <p className="text-xs text-gray-600 mt-2 leading-snug italic" data-testid={`ai-reasoning-${i}`}>
                      &ldquo;{p.reasoning}&rdquo;
                    </p>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[11px] text-gray-500">{p.kickoff}</span>
                    {typeof p.confidence === "number" && (
                      <span className={`text-xs font-bold ${style.text} px-2 py-0.5 rounded-full ${style.bg}`}>
                        {p.confidence}% confidence
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Buy Now CTA — surfaces the paid packages right after the AI verdicts */}
        <div className="mt-8 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500 rounded-2xl p-6 sm:p-8 text-center text-gray-900 relative overflow-hidden shadow-lg" data-testid="ai-buy-now-cta">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22%23000%22%20fill-opacity%3D%220.04%22%3E%3Ccircle%20cx%3D%2210%22%20cy%3D%2210%22%20r%3D%222%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E')]" />
          <div className="relative z-10">
            <Trophy className="h-8 w-8 mx-auto mb-2" />
            <h3 className="text-xl sm:text-2xl font-extrabold mb-2">Want the FULL Slip — Not Just the AI Hints?</h3>
            <p className="text-sm sm:text-base mb-5 max-w-xl mx-auto text-gray-900/90">
              Purchase an authentic game from here to win big this weekend. Authentic, hand-picked fixed draws with one-time secure code.
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/buy")}
              className="bg-gray-900 hover:bg-black text-amber-300 font-extrabold rounded-full px-8 h-12 text-base shadow-xl"
              data-testid="ai-buy-now-btn"
            >
              Buy Now — Win Big <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AiInsightsSection;
